import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  ArrowDown, ArrowLeftRight, ArrowUp, ChevronDown, ChevronRight, Columns3, Eye, Filter, KeyRound, Layers, Save, Trash2, Undo2, X,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  applyLocalEdit, bulkColumns, cellText, deleteView, formatAgo, formatDateTime, hiddenColumns, livenessOf, loadViews,
  nextSort, saveView, toServerValue, viewRows, MISSING_ALERT } from '../../lib/inventoryTable';
import { inventoryApi, errorText } from '../../lib/inventoryApi';

/*
 * THE reusable inventory table (spec 01.4 section 6): one component for every tab, driven by a column config
 * (lib/inventoryTable.js). Sort (click; shift-click adds a key), a filter row, free-text search, group-by with
 * collapsible groups, inline edit (double-click), row selection + bulk edit, linked-record chips that click through,
 * saved views in localStorage. Access is the SERVER's job: this only hides edit affordances the viewer cannot use.
 *
 * Controlled view: `view` = {q, filters, sort, groupBy, hidden}; every change goes through onViewChange.
 */

export function Chip({ chip, onClick, muted }) {
  if (!chip) return null;
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick?.(chip); }}
      title={`Open ${chip.label}`}
      data-testid="inv-chip"
      className={cn(
        'inline-flex max-w-[180px] items-center rounded-full border px-2 py-0.5 text-[11px] leading-4 truncate transition-colors',
        muted ? 'border-border text-muted-foreground' : 'border-primary/30 bg-primary/10 text-foreground hover:bg-primary/20'
      )}
    >
      <span className="truncate">{chip.label}</span>
    </button>
  );
}

/**
 * Type-ahead picker over GET /inventory/lookup/{ref}. onPick(chip), or onPick(null) to clear (only offered when
 * allowClear - the bulk bar clears through its own confirmed "Clear field" instead).
 *
 * INV-7b browser drive: a picker opened on purpose (autoFocus: a cell editor, "Pick slot", the Capacity client) shows
 * its list at once, as before. A picker that sits in a form (autoFocus false: the bulk bar, the Add dialog) shows its
 * list only while focus is inside it - it used to be open all the time, covering the selected rows and spilling out
 * of the Add dialog, and its Cancel did nothing. The lookup runs only while the list is open.
 *
 * INV-8 (re-review 3 should-fix 4): usable from the keyboard. Arrow Down / Up move through the matches, Enter picks the
 * highlighted one, Escape cancels; Tab moves into the list (its buttons take Enter / Space) - the list closes only when
 * focus leaves the WHOLE picker (it closed on the input's blur, so Tab into the options closed it first).
 */
export function LinkPicker({ refTable, current, onPick, onCancel, autoFocus = true, allowClear = true }) {
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(!!autoFocus);
  const [active, setActive] = useState(-1);
  const listId = useId();
  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    const h = setTimeout(() => {
      // Promise.resolve first: a lookup that throws (or returns nothing) shows as the list's error, never an uncaught throw.
      Promise.resolve().then(() => inventoryApi.lookup(refTable, q))
        .then((r) => {
          if (!alive) return;
          const next = Array.isArray(r?.data) ? r.data : [];
          setItems(next);
          setActive((a) => Math.min(a, next.length - 1));
          setError(null);
        })
        .catch((e) => { if (alive) setError(errorText(e)); });
    }, 200);
    return () => { alive = false; clearTimeout(h); };
  }, [refTable, q, open]);
  // An on-purpose picker is closed by its owner (onCancel / onPick unmount it); an in-form one closes itself.
  const close = () => { if (!autoFocus) { setOpen(false); setActive(-1); } };
  const pick = (c) => { close(); onPick(c); };
  const cancel = () => { close(); onCancel?.(); };
  const optionId = (i) => `${listId}-opt-${i}`;
  useEffect(() => {
    if (active < 0) return;
    const el = typeof document !== 'undefined' ? document.getElementById(`${listId}-opt-${active}`) : null;
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [active, listId]);
  const onInputKey = (e) => {
    if (e.key === 'Escape') { cancel(); return; }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActive((a) => Math.min(a + 1, items.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => (items.length ? Math.max(a - 1, 0) : -1));
    } else if (e.key === 'Enter' && open && active >= 0 && active < items.length) {
      e.preventDefault();
      pick(items[active]);
    }
  };
  // Enter / Space on a list button (reached with Tab) does what a click does; Escape cancels.
  const keyAct = (fn) => (e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fn(); }
    else if (e.key === 'Escape') { e.preventDefault(); cancel(); }
  };
  return (
    <div
      className="relative min-w-[200px]"
      data-testid="inv-link-picker"
      onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) close(); }}
    >
      <input
        autoFocus={autoFocus}
        value={q}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && active >= 0 ? optionId(active) : undefined}
        onFocus={() => setOpen(true)}
        onChange={(e) => { setQ(e.target.value); setOpen(true); setActive(-1); }}
        onKeyDown={onInputKey}
        placeholder={current ? `Replace ${current}` : 'Search…'}
        className="w-full rounded-md border border-border bg-background px-2 py-1 text-xs"
      />
      {open && (
        <div
          id={listId}
          role="listbox"
          onMouseDown={(e) => e.preventDefault()}
          className="absolute z-30 mt-1 max-h-56 w-full overflow-auto rounded-md border border-border bg-popover shadow-lg"
          data-testid="inv-link-picker-list"
        >
          {error && <div className="px-2 py-1.5 text-xs text-danger">{error}</div>}
          {items.map((c, i) => (
            <button
              type="button"
              key={`${c.table}-${c.id}`}
              id={optionId(i)}
              role="option"
              aria-selected={i === active}
              data-active={i === active ? 'true' : undefined}
              onMouseDown={(e) => { e.preventDefault(); pick(c); }}
              onKeyDown={keyAct(() => pick(c))}
              onMouseEnter={() => setActive(i)}
              className={cn('block w-full truncate px-2 py-1.5 text-left text-xs hover:bg-secondary focus:bg-secondary focus:outline-none',
                i === active && 'bg-secondary')}
            >
              {c.label}
            </button>
          ))}
          {!error && items.length === 0 && <div className="px-2 py-1.5 text-xs text-muted-foreground">No match</div>}
          <div className="flex justify-between border-t border-border">
            {allowClear ? (
              <button type="button" onMouseDown={(e) => { e.preventDefault(); pick(null); }} onKeyDown={keyAct(() => pick(null))}
                className="px-2 py-1 text-xs text-muted-foreground hover:text-foreground focus:text-foreground">
                Clear link
              </button>
            ) : <span />}
            <button type="button" onMouseDown={(e) => { e.preventDefault(); cancel(); }} onKeyDown={keyAct(cancel)}
              className="px-2 py-1 text-xs text-muted-foreground hover:text-foreground focus:text-foreground">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** The editor for one cell kind. onSave(value, chip?) / onCancel(). */
export function CellEditor({ col, initial, onSave, onCancel }) {
  const [value, setValue] = useState(initial ?? '');
  const done = useRef(false);
  const save = (v, chip) => {
    if (done.current) return;
    done.current = true;
    onSave(v, chip);
  };
  if (col.kind === 'ref') {
    return <LinkPicker refTable={col.ref} current={initial} onPick={(chip) => save(chip ? chip.id : null, chip)} onCancel={onCancel} />;
  }
  if (col.kind === 'enum') {
    return (
      <select
        autoFocus
        aria-label={col.label}
        value={value ?? ''}
        onChange={(e) => save(e.target.value === '' ? null : e.target.value)}
        onBlur={onCancel}
        onKeyDown={(e) => { if (e.key === 'Escape') onCancel(); }}
        className="w-full rounded-md border border-border bg-background px-1 py-1 text-xs"
      >
        <option value="">(empty)</option>
        {(col.values || []).map((v) => <option key={v} value={v}>{v}</option>)}
      </select>
    );
  }
  if (col.kind === 'bool') {
    return (
      <input
        autoFocus
        type="checkbox"
        aria-label={col.label}
        defaultChecked={initial === true}
        onChange={(e) => save(e.target.checked)}
        onBlur={onCancel}
      />
    );
  }
  const type = col.kind === 'date' ? 'date' : col.kind === 'int' || col.kind === 'double' ? 'number' : 'text';
  return (
    <input
      autoFocus
      type={type}
      aria-label={col.label}
      value={value ?? ''}
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') save(value);
        if (e.key === 'Escape') { done.current = true; onCancel(); }
      }}
      onBlur={() => save(value)}
      className="w-full rounded-md border border-primary/50 bg-background px-1.5 py-1 text-xs"
    />
  );
}

/**
 * Phase 2: when the launcher last reported the phone (or the PC). A phone in service that its PC no longer reports
 * gets the red "missing > N min" badge (N = the server's threshold, 30 by default); a spare / repair / retired phone
 * that is away only reads "missing" in amber.
 */
export function LivenessBadge({ row, missingMinutes = 30 }) {
  const { state, minutes, alert } = livenessOf(row);
  const ago = formatAgo(minutes);
  const seen = row?.lastSeenAt ? ` (last report ${formatDateTime(row.lastSeenAt)})` : '';
  if (state === 'online') {
    return (
      <span className="inline-flex items-center gap-1.5 text-[11px]" title={`Reported ${ago === 'just now' ? 'just now' : `${ago} ago`}${seen}`}
        data-testid="inv-liveness" data-state="online">
        <span className="h-2 w-2 rounded-full bg-success" aria-hidden="true" />
        online{ago ? <span className="tnum text-muted-foreground">· {ago}</span> : null}
      </span>
    );
  }
  if (state === 'missing') {
    return (
      <span className="inline-flex items-center gap-1 text-[11px]" data-testid="inv-liveness" data-state="missing"
        title={`Not reported for ${ago} while its PC still reports${seen}. Unplugged, adb lost it, or the phone is off.`}>
        <span data-testid={alert ? 'inv-missing-badge' : undefined}
          className={cn('rounded-md px-1.5 py-0.5 font-medium', alert ? 'bg-danger/15 text-danger' : 'bg-warning/15 text-warning')}>
          {alert ? `missing > ${missingMinutes} min` : 'missing'}
        </span>
        <span className="tnum text-muted-foreground">{ago}</span>
      </span>
    );
  }
  if (state === 'unreported') {
    return (
      <span className="inline-flex items-center gap-1 text-[11px]" data-testid="inv-liveness" data-state="unreported"
        title={`Its PC's launcher sees this phone but may not report it: it belongs to another client, or it holds another client's account. Its data is as last reported${seen}.`}>
        <span className="rounded-md bg-secondary px-1.5 py-0.5 font-medium text-muted-foreground">seen, not reported</span>
        {ago ? <span className="tnum text-muted-foreground">{ago}</span> : null}
      </span>
    );
  }
  if (state === 'offline') {
    return (
      <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground" data-testid="inv-liveness" data-state="offline"
        title={`Not reported for ${ago}; its PC is not reporting either${seen}`}>
        <span className="h-2 w-2 rounded-full bg-muted-foreground/50" aria-hidden="true" />
        offline <span className="tnum">· {ago}</span>
      </span>
    );
  }
  return <span className="text-[11px] text-muted-foreground/70" data-testid="inv-liveness" data-state="unknown">never reported</span>;
}

function Cell({ table, row, col, onChipClick, onSecret }) {
  if (col.kind === 'liveness') return <LivenessBadge row={row} missingMinutes={col.missingMinutes} />;
  if (col.kind === 'chips') {
    const v = row.links?.[col.link];
    const list = Array.isArray(v) ? v : v ? [v] : [];
    if (list.length === 0) return <span className="text-muted-foreground/50">—</span>;
    return (
      <div className="flex flex-wrap gap-1">
        {list.slice(0, 4).map((c) => <Chip key={`${c.table}-${c.id}`} chip={c} onClick={onChipClick} />)}
        {list.length > 4 && <span className="text-[11px] text-muted-foreground">+{list.length - 4}</span>}
      </div>
    );
  }
  if (col.kind === 'ref') {
    const chip = row.links?.[col.link];
    if (chip) return <Chip chip={chip} onClick={onChipClick} />;
    const id = row[col.field];
    return id == null ? <span className="text-muted-foreground/50">—</span> : <span className="text-muted-foreground">#{id}</span>;
  }
  if (col.kind === 'count') {
    const n = row.links?.[col.link] ?? 0;
    if (!onChipClick || !n) return <span className="tnum">{n}</span>;
    return (
      <button type="button" className="tnum underline decoration-dotted underline-offset-2 hover:text-primary"
        title={`Show these ${col.label.toLowerCase()}`}
        onClick={(e) => { e.stopPropagation(); onChipClick({ table: '__count', from: table, link: col.link, id: row.id, label: String(n) }); }}>
        {n}
      </button>
    );
  }
  if (col.kind === 'secret') {
    const flags = col.key === 'credential' ? row.links?.credential || {} : { password: !!row.hasSecret };
    const on = Object.entries(flags).filter(([, v]) => v).map(([k]) => k);
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onSecret?.(row); }}
        className={cn('inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px]',
          on.length ? 'text-foreground hover:bg-secondary' : 'text-muted-foreground hover:bg-secondary')}
        title={on.length ? `Stored: ${on.join(', ')}` : 'No login stored'}
        data-testid="inv-secret"
      >
        <KeyRound className="h-3.5 w-3.5" />
        {on.length ? `${on.length} stored` : 'none'}
      </button>
    );
  }
  if (col.kind === 'datetime') return <span className="tnum text-muted-foreground">{formatDateTime(row[col.field])}</span>;
  if (col.kind === 'enum' && row[col.field]) {
    return <span className={cn('rounded-md px-1.5 py-0.5 text-[11px] font-medium', enumTone(row[col.field]))}>{row[col.field]}</span>;
  }
  const text = cellText(row, col);
  if (text === '') return <span className="text-muted-foreground/50">—</span>;
  return <span className={cn(col.kind === 'longtext' && 'line-clamp-2', (col.kind === 'int' || col.kind === 'double') && 'tnum')}>{text}</span>;
}

const TONES = {
  active: 'bg-success/15 text-success', new: 'bg-info/15 text-info', challenged: 'bg-warning/15 text-warning',
  locked: 'bg-warning/15 text-warning', banned: 'bg-danger/15 text-danger', disabled: 'bg-muted text-muted-foreground',
  logged_out: 'bg-warning/15 text-warning',
  repair: 'bg-warning/15 text-warning', retired: 'bg-muted text-muted-foreground', spare: 'bg-info/15 text-info',
  expired: 'bg-danger/15 text-danger', inactive: 'bg-muted text-muted-foreground', paused: 'bg-warning/15 text-warning',
  ended: 'bg-muted text-muted-foreground', INSTAGRAM: 'bg-primary/15 text-primary', TIKTOK: 'bg-info/15 text-info',
};
function enumTone(v) {
  return TONES[v] || 'bg-secondary text-foreground';
}

/**
 * The bulk edit bar shown while rows are selected. Apply needs a chosen value - it never sends an empty one; emptying
 * a field on every selected row is the separate "Clear field", which asks first (review must-fix 6: one click on
 * Apply with nothing chosen used to wipe the field on all selected rows).
 */
function BulkBar({ table, columns, count, onApply, onGroups, onClear }) {
  const editable = useMemo(() => bulkColumns(columns), [columns]);
  const [key, setKey] = useState(editable[0]?.key || '');
  const [value, setValue] = useState('');
  const [chip, setChip] = useState(null);
  const [groupChip, setGroupChip] = useState(null);
  const col = editable.find((c) => c.key === key);
  const chosen = !col ? null
    : col.kind === 'ref' ? (chip ? chip.id : null)
      : String(value ?? '').trim() === '' ? null : toServerValue(col, value);
  const hasValue = chosen !== null && chosen !== undefined && chosen !== '';
  const rowsText = `${count} selected row${count === 1 ? '' : 's'}`;
  const clearField = () => {
    if (!col) return;
    if (!window.confirm(`Clear "${col.label}" on ${rowsText}? Every selected row loses its value.`)) return;
    onApply(col, null);
  };
  return (
    <div className="mb-2 flex flex-wrap items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-xs" data-testid="inv-bulk-bar">
      <span className="font-medium">{count} selected</span>
      {editable.length > 0 && (
        <>
          <span className="text-muted-foreground">Set</span>
          <select aria-label="Bulk field" value={key} onChange={(e) => { setKey(e.target.value); setValue(''); setChip(null); }}
            className="rounded-md border border-border bg-background px-1 py-1">
            {editable.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
          <span className="text-muted-foreground">to</span>
          {col?.kind === 'enum' ? (
            <select aria-label="Bulk value" value={value} onChange={(e) => setValue(e.target.value)} className="rounded-md border border-border bg-background px-1 py-1">
              <option value="">(empty)</option>
              {(col.values || []).map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          ) : col?.kind === 'ref' ? (
            chip ? <Chip chip={chip} onClick={() => setChip(null)} /> : <LinkPicker refTable={col.ref} autoFocus={false} allowClear={false} onPick={(c) => { if (c) setChip(c); }} />
          ) : (
            <input aria-label="Bulk value" value={value} onChange={(e) => setValue(e.target.value)}
              type={col?.kind === 'date' ? 'date' : 'text'} className="rounded-md border border-border bg-background px-1.5 py-1" />
          )}
          <button type="button" disabled={!hasValue} title={hasValue ? `Set ${col?.label} on ${rowsText}` : 'Choose a value first'}
            className="rounded-md bg-primary px-2 py-1 font-medium text-primary-foreground disabled:opacity-40"
            onClick={() => { if (hasValue) onApply(col, chosen); }}>
            Apply
          </button>
          <button type="button" title={`Empty ${col?.label} on ${rowsText} (asks first)`}
            className="rounded-md border border-border px-2 py-1 text-muted-foreground hover:text-danger"
            onClick={clearField}>
            Clear field
          </button>
        </>
      )}
      {table === 'accounts' && (
        // INV-7b browser drive: wraps at 390 px - "Remove" ran 3 px past the bar (and the page) on a phone.
        <span className="flex max-w-full flex-wrap items-center gap-1.5 border-l border-border pl-2">
          <Layers className="h-3.5 w-3.5 text-muted-foreground" />
          {groupChip ? <Chip chip={groupChip} onClick={() => setGroupChip(null)} /> : <LinkPicker refTable="groups" autoFocus={false} allowClear={false} onPick={(c) => { if (c) setGroupChip(c); }} />}
          <button type="button" disabled={!groupChip} className="whitespace-nowrap rounded-md border border-border px-2 py-1 disabled:opacity-40" onClick={() => onGroups([groupChip.id], [])}>Add to group</button>
          <button type="button" disabled={!groupChip} className="whitespace-nowrap rounded-md border border-border px-2 py-1 disabled:opacity-40" onClick={() => onGroups([], [groupChip.id])}>Remove</button>
        </span>
      )}
      <button type="button" onClick={onClear} className="ml-auto inline-flex items-center gap-1 text-muted-foreground hover:text-foreground">
        <X className="h-3.5 w-3.5" /> Clear selection
      </button>
    </div>
  );
}

export default function InventoryTable({
  table, columns, rows, total, loading, isAdmin, view, onViewChange, onCellSave, onBulkSet, onBulkGroups,
  onChipClick, onSecret, onArchive, onRestore, onAssign, showArchived, toolbar,
}) {
  // Phase 3: the admin's row actions - Assign / move (accounts), archive / restore.
  const hasActions = isAdmin && !!(onArchive || onRestore || onAssign);
  const [selected, setSelected] = useState(() => new Set());
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [editing, setEditing] = useState(null); // {id, key}
  const [views, setViews] = useState(() => loadViews(table));
  const [showColumns, setShowColumns] = useState(false);
  const [showFilters, setShowFilters] = useState(() => Object.keys(view?.filters || {}).length > 0);
  const [localRows, setLocalRows] = useState(rows);

  useEffect(() => { setLocalRows(rows); }, [rows]);
  useEffect(() => { setViews(loadViews(table)); setSelected(new Set()); setEditing(null); }, [table]);

  const hidden = hiddenColumns(columns, view);
  const visibleCols = columns.filter((c) => !hidden.has(c.key));
  const groups = useMemo(() => viewRows(localRows || [], visibleCols, view), [localRows, visibleCols, view]);
  const shownCount = groups.reduce((n, g) => n + g.rows.length, 0);
  const canSelect = isAdmin && !!onBulkSet;
  const setView = (patch) => onViewChange({ ...view, ...patch });

  const toggleRow = (id) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const allIds = groups.flatMap((g) => g.rows.map((r) => r.id));
  const allSelected = allIds.length > 0 && allIds.every((id) => selected.has(id));
  // A row the search / filters hide leaves the selection, so a bulk edit only reaches rows on screen (must-fix 6).
  const shownKey = allIds.join(',');
  useEffect(() => {
    setSelected((s) => {
      if (s.size === 0) return s;
      const shown = new Set(shownKey === '' ? [] : shownKey.split(','));
      const keep = [...s].filter((id) => shown.has(String(id)));
      return keep.length === s.size ? s : new Set(keep);
    });
  }, [shownKey]);

  const saveCell = async (row, col, raw, chip) => {
    setEditing(null);
    const value = col.kind === 'ref' ? raw : toServerValue(col, raw);
    if ((row[col.field] ?? null) === (value ?? null)) return;
    const before = localRows;
    setLocalRows((rs) => rs.map((r) => (r.id === row.id ? applyLocalEdit(r, col, value, chip) : r)));
    try {
      const fresh = await onCellSave(row, col, value);
      if (fresh) setLocalRows((rs) => rs.map((r) => (r.id === row.id ? fresh : r)));
    } catch (e) {
      setLocalRows(before); // roll back the optimistic edit; the page shows the server's reason
    }
  };

  return (
    <div data-testid={`inv-table-${table}`}>
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <input
          value={view?.q || ''}
          onChange={(e) => setView({ q: e.target.value })}
          placeholder="Search…"
          aria-label="Search"
          className="h-8 w-48 rounded-md border border-border bg-background px-2 text-sm"
        />
        <button type="button" onClick={() => setShowFilters((s) => !s)}
          className={cn('inline-flex h-8 items-center gap-1 rounded-md border px-2 text-xs', showFilters ? 'border-primary/50 text-foreground' : 'border-border text-muted-foreground')}>
          <Filter className="h-3.5 w-3.5" /> Filters
        </button>
        <label className="inline-flex h-8 items-center gap-1 rounded-md border border-border px-2 text-xs text-muted-foreground">
          <Layers className="h-3.5 w-3.5" />
          <span>Group</span>
          <select aria-label="Group by" value={view?.groupBy || ''} onChange={(e) => setView({ groupBy: e.target.value })} className="bg-transparent text-foreground">
            <option value="">none</option>
            {visibleCols.filter((c) => c.groupable).map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
        </label>
        <div className="relative">
          <button type="button" onClick={() => setShowColumns((s) => !s)} className="inline-flex h-8 items-center gap-1 rounded-md border border-border px-2 text-xs text-muted-foreground">
            <Columns3 className="h-3.5 w-3.5" /> Columns
          </button>
          {showColumns && (
            <div className="absolute z-30 mt-1 w-48 rounded-md border border-border bg-popover p-2 shadow-lg">
              {columns.map((c) => (
                <label key={c.key} className="flex items-center gap-2 py-0.5 text-xs">
                  <input type="checkbox" checked={!hidden.has(c.key)}
                    onChange={(e) => {
                      const next = new Set(hidden);
                      if (e.target.checked) next.delete(c.key); else next.add(c.key);
                      setView({ hidden: [...next] });
                    }} />
                  {c.label}
                </label>
              ))}
            </div>
          )}
        </div>
        <label className="inline-flex h-8 items-center gap-1 rounded-md border border-border px-2 text-xs text-muted-foreground">
          <Eye className="h-3.5 w-3.5" />
          <select aria-label="Saved views" value="" onChange={(e) => {
            const v = views.find((x) => x.name === e.target.value);
            if (v) onViewChange({ ...v.view });
          }} className="bg-transparent text-foreground">
            <option value="">Views ({views.length})</option>
            {views.map((v) => <option key={v.name} value={v.name}>{v.name}</option>)}
          </select>
        </label>
        <button type="button" title="Save this view" className="inline-flex h-8 items-center gap-1 rounded-md border border-border px-2 text-xs text-muted-foreground"
          onClick={() => {
            const name = window.prompt('Name this view (filters, sort, grouping and columns are saved in this browser):');
            if (name) setViews(saveView(table, name, view));
          }}>
          <Save className="h-3.5 w-3.5" /> Save view
        </button>
        {views.length > 0 && (
          <button type="button" title="Delete a saved view" className="inline-flex h-8 items-center rounded-md px-1 text-xs text-muted-foreground"
            onClick={() => {
              const name = window.prompt(`Delete which view? (${views.map((v) => v.name).join(', ')})`);
              if (name) setViews(deleteView(table, name));
            }}>
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
        <div className="ml-auto flex flex-wrap items-center gap-2">{toolbar}</div>
      </div>

      {canSelect && selected.size > 0 && (
        <BulkBar
          table={table}
          columns={columns}
          count={selected.size}
          onClear={() => setSelected(new Set())}
          onApply={async (col, value) => { await onBulkSet([...selected], col, value); }}
          onGroups={async (add, remove) => { await onBulkGroups?.([...selected], add, remove); }}
        />
      )}

      <div className="text-[11px] text-muted-foreground mb-1" data-testid="inv-count">
        {loading ? 'Loading…' : `${shownCount} shown${total != null && total > (localRows || []).length ? ` · ${(localRows || []).length} of ${total} loaded - narrow with a link filter` : ''}`}
      </div>

      <div className="table-scroll rounded-lg border border-border">
        <table className="w-full text-xs">
          <thead className="bg-secondary/40">
            <tr>
              {canSelect && (
                <th className="w-8 px-2 py-2">
                  <input type="checkbox" aria-label="Select all" checked={allSelected}
                    onChange={() => setSelected(allSelected ? new Set() : new Set(allIds))} />
                </th>
              )}
              {visibleCols.map((c) => {
                const s = (view?.sort || []).find((x) => x.key === c.key);
                return (
                  <th key={c.key} style={{ minWidth: c.width }} className="px-2 py-2 text-left font-medium text-muted-foreground">
                    <button type="button" className="inline-flex items-center gap-1 hover:text-foreground"
                      title="Click to sort, shift-click to add a sort key"
                      onClick={(e) => setView({ sort: nextSort(view?.sort, c.key, e.shiftKey) })}>
                      {c.label}
                      {s && (s.dir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
                      {s && (view.sort.length > 1) && <span className="text-[10px]">{view.sort.indexOf(s) + 1}</span>}
                    </button>
                  </th>
                );
              })}
              {hasActions && <th className="w-14" />}
            </tr>
            {showFilters && (
              <tr>
                {canSelect && <th />}
                {visibleCols.map((c) => (
                  <th key={c.key} className="px-1 pb-1.5">
                    {c.kind === 'enum' || c.kind === 'bool' || c.kind === 'liveness' ? (
                      <select aria-label={`Filter ${c.label}`} value={view?.filters?.[c.key] || ''}
                        onChange={(e) => setView({ filters: { ...(view?.filters || {}), [c.key]: e.target.value } })}
                        className="w-full rounded border border-border bg-background px-1 py-0.5 font-normal">
                        <option value="">any</option>
                        {c.kind !== 'liveness' && <option value="(empty)">(empty)</option>}
                        {(c.kind === 'bool' ? ['yes', 'no'] : c.values || []).map((v) => <option key={v} value={v}>{v}</option>)}
                        {c.kind === 'liveness' && (c.values || []).includes('missing') && <option value={MISSING_ALERT}>{MISSING_ALERT}</option>}
                      </select>
                    ) : (
                      <input aria-label={`Filter ${c.label}`} value={view?.filters?.[c.key] || ''}
                        onChange={(e) => setView({ filters: { ...(view?.filters || {}), [c.key]: e.target.value } })}
                        placeholder="contains…" className="w-full rounded border border-border bg-background px-1 py-0.5 font-normal" />
                    )}
                  </th>
                ))}
                {hasActions && <th />}
              </tr>
            )}
          </thead>
          <tbody>
            {groups.map((g) => {
              const isCollapsed = collapsed.has(g.key);
              return (
                <React.Fragment key={g.key}>
                  {g.label != null && (
                    <tr className="bg-secondary/25" data-testid="inv-group">
                      <td colSpan={visibleCols.length + (canSelect ? 1 : 0) + (hasActions ? 1 : 0)} className="px-2 py-1.5">
                        <button type="button" className="inline-flex items-center gap-1.5 font-medium"
                          onClick={() => setCollapsed((s) => { const n = new Set(s); if (n.has(g.key)) n.delete(g.key); else n.add(g.key); return n; })}>
                          {isCollapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                          {g.label}
                          <span className="rounded-full bg-secondary px-1.5 text-[10px] text-muted-foreground">{g.rows.length}</span>
                        </button>
                      </td>
                    </tr>
                  )}
                  {!isCollapsed && g.rows.map((row) => (
                    <tr key={`${g.key}-${row.id}`} data-testid="inv-row" className={cn('border-t border-border/60 align-top', row.archivedAt && 'opacity-60', selected.has(row.id) && 'bg-primary/5')}>
                      {canSelect && (
                        <td className="px-2 py-1.5">
                          <input type="checkbox" aria-label={`Select ${row.label}`} checked={selected.has(row.id)} onChange={() => toggleRow(row.id)} />
                        </td>
                      )}
                      {visibleCols.map((c) => {
                        const isEditing = editing && editing.id === row.id && editing.key === c.key;
                        return (
                          <td key={c.key} className={cn('px-2 py-1.5', c.editable && isAdmin && 'cursor-text')}
                            title={c.editable && isAdmin ? 'Double-click to edit' : undefined}
                            onDoubleClick={() => { if (c.editable && isAdmin && !row.archivedAt) setEditing({ id: row.id, key: c.key }); }}>
                            {isEditing ? (
                              <CellEditor col={c}
                                initial={c.kind === 'ref' ? row.links?.[c.link]?.label : row[c.field]}
                                onSave={(v, chip) => saveCell(row, c, v, chip)}
                                onCancel={() => setEditing(null)} />
                            ) : (
                              <Cell table={table} row={row} col={c} onChipClick={onChipClick} onSecret={onSecret} />
                            )}
                          </td>
                        );
                      })}
                      {hasActions && (
                        <td className="whitespace-nowrap px-1 py-1.5 text-right">
                          {onAssign && !row.archivedAt && (
                            <button type="button" title="Assign / move to a profile" aria-label={`Assign ${row.label}`} onClick={() => onAssign(row)}
                              className="mr-1.5 text-muted-foreground hover:text-foreground"><ArrowLeftRight className="h-3.5 w-3.5" /></button>
                          )}
                          {row.archivedAt ? (
                            onRestore && <button type="button" title="Restore" aria-label={`Restore ${row.label}`} onClick={() => onRestore(row)} className="text-muted-foreground hover:text-foreground"><Undo2 className="h-3.5 w-3.5" /></button>
                          ) : (
                            onArchive && <button type="button" title="Archive" aria-label={`Archive ${row.label}`} onClick={() => onArchive(row)} className="text-muted-foreground hover:text-danger"><Trash2 className="h-3.5 w-3.5" /></button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
            {!loading && shownCount === 0 && (
              <tr><td colSpan={visibleCols.length + 2} className="px-3 py-6 text-center text-muted-foreground">
                {showArchived ? 'No archived rows.' : 'Nothing here yet.'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export { errorText };
